import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Heart, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ONE_TIME = [25, 50, 100, 250];
const MONTHLY = [5, 10, 25, 50];

const Donate = () => {
  const [frequency, setFrequency] = useState("one_time");
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const presets = frequency === "monthly" ? MONTHLY : ONE_TIME;
  const finalAmount = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    if (!finalAmount || finalAmount < 1) { toast.error("Please enter an amount of at least $1"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        amount: finalAmount,
        frequency,
        origin_url: window.location.origin,
        donor_name: name,
        donor_email: email,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="donate-page">
      <div className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <div className="w-14 h-14 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Heart className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Support Our Mission
          </h1>
          <p className="text-slate-300 text-base max-w-xl mx-auto">
            Your gift helps unemployed and underserved job seekers access professional interview
            attire — restoring dignity and opening doors to employment.
          </p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <Card>
          <CardContent className="p-8 space-y-6">
            {/* Frequency toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-lg">
              {[["one_time", "One-time"], ["monthly", "Monthly"]].map(([val, label]) => (
                <button key={val} type="button" onClick={() => { setFrequency(val); setCustom(""); setAmount(val === "monthly" ? 10 : 50); }}
                  data-testid={`freq-${val}`}
                  className={`py-2.5 rounded-md text-sm font-medium transition-colors ${frequency === val ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Presets */}
            <div>
              <Label className="mb-2 block">Choose an amount {frequency === "monthly" && <span className="text-slate-400">/ month</span>}</Label>
              <div className="grid grid-cols-4 gap-2">
                {presets.map((p) => (
                  <button key={p} type="button" onClick={() => { setAmount(p); setCustom(""); }}
                    data-testid={`amount-${p}`}
                    className={`py-3 rounded-lg border text-sm font-semibold transition-colors ${!custom && amount === p ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-700 border-slate-200 hover:border-amber-400"}`}>
                    ${p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="custom">Or enter a custom amount</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <Input id="custom" type="number" min="1" className="pl-7" data-testid="custom-amount"
                  placeholder="Custom amount" value={custom} onChange={(e) => setCustom(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div><Label htmlFor="dname">Name (optional)</Label><Input id="dname" className="mt-1" data-testid="donor-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div><Label htmlFor="demail">Email (optional, for receipt)</Label><Input id="demail" type="email" className="mt-1" data-testid="donor-email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            </div>

            <Button onClick={donate} disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-base gap-2" data-testid="donate-submit">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to secure checkout...</> : <><Heart className="w-5 h-5" /> Donate ${finalAmount || 0}{frequency === "monthly" ? "/mo" : ""}</>}
            </Button>

            <p className="text-xs text-slate-500 flex items-center gap-1.5 justify-center">
              <ShieldCheck className="w-3.5 h-3.5" /> Payments are securely processed by Stripe. Your card details never touch our servers.
            </p>

            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed" data-testid="tax-note">
              Soar Your Visibility–Dress to Impress is a nonprofit serving the local community. It is
              through our fiscal sponsorship that all donations are tax deductible. Please let us know
              if you need a tax receipt with details; otherwise you will receive a brief email receipt
              to confirm receipt of your donation.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Donate;
